import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, Megaphone, Pause, Play, Plus, Send, Sparkles, Search } from "lucide-react";
import { api, formatApiError } from "../api";
import { MARKETING } from "../constants/testIds";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { toast } from "../components/ui/sonner";
import { CampaignWizard } from "../components/marketing/CampaignWizard";
import { CampaignDetail } from "../components/marketing/CampaignDetail";
import { AdPreview } from "../components/marketing/AdPreview";
import { AttributionTab } from "../components/marketing/AttributionTab";
import { useAppData } from "../contexts/AppDataContext";

const TABS = [
  { key: "campaigns", label: "Campaigns" },
  { key: "templates", label: "Templates" },
  { key: "ideas", label: "Ad Ideas" },
  { key: "roi", label: "Revenue & ROI" },
  { key: "analytics", label: "Analytics" },
  { key: "settings", label: "Settings" },
];

const STATUS_PILL = { active: "active", paused: "expiring", draft: "expiring", completed: "active", archived: "overdue" };

export const rupees = (paise) => (paise == null ? "—" : `₹${(paise / 100).toLocaleString("en-IN")}`);

// Marketing & Ads. Every figure shown here is measured from this gym's own records — recorded
// ad spend and real leads attributed to a campaign. Platform-side numbers (impressions, clicks,
// CTR) live inside Meta/Google and are shown as unavailable until an ad account is connected,
// rather than being estimated or filled in with plausible-looking values.
export default function Marketing() {
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("campaigns");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardTemplate, setWizardTemplate] = useState(null);
  const [openCampaign, setOpenCampaign] = useState(null);

  const analyticsQ = useQuery({ queryKey: ["marketing-analytics"], queryFn: async () => (await api.get("/marketing/analytics")).data });
  const attributionQ = useQuery({ queryKey: ["marketing-attribution"], queryFn: async () => (await api.get("/marketing/attribution")).data });
  const summary = analyticsQ.data?.summary;
  const totals = attributionQ.data?.totals;

  const openWizard = (template = null) => { setWizardTemplate(template); setWizardOpen(true); };

  return (
    <div data-testid={MARKETING.page} className="space-y-6 max-w-5xl pb-8">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>
            <Megaphone size={22} style={{ color: "var(--scarlet-accent)" }} /> Marketing
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>
            Run ad campaigns and see what they actually cost you per lead.
          </p>
        </div>
        <Button data-testid={MARKETING.newCampaignButton} onClick={() => openWizard(null)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
          <Plus size={15} /> Create campaign
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat testId={MARKETING.summaryActiveCampaigns} label="Active campaigns" value={summary ? summary.active_campaigns : "—"} />
        <Stat testId={MARKETING.summarySpend} label="Spend this month" value={summary ? rupees(summary.month_spend) : "—"} />
        <Stat testId={MARKETING.summaryLeads} label="Leads generated" value={summary ? summary.leads_generated : "—"} />
        <Stat testId={MARKETING.summaryCpl} label="Cost per lead" value={summary ? rupees(summary.cost_per_lead) : "—"} />
        <Stat
          testId={MARKETING.summaryRoi} label="Return on ad spend"
          value={totals?.roi_multiple != null ? `${totals.roi_multiple}×` : "—"}
          accent={totals?.roi_multiple != null && totals.roi_multiple >= 1}
          onClick={() => setTab("roi")}
        />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={MARKETING.tab(t.key)} onClick={() => setTab(t.key)}
            className="px-3.5 py-2 rounded-[10px] text-sm font-medium transition-colors"
            style={tab === t.key
              ? { background: "var(--scarlet-accent)", color: "#fff" }
              : { background: "var(--scarlet-surface)", color: "var(--scarlet-text-secondary)", border: "1px solid var(--scarlet-hairline)" }}
          >
            {t.label}
          </button>
        ))}
      </motion.div>

      {tab === "campaigns" && <CampaignsTab onOpen={setOpenCampaign} onNew={() => openWizard(null)} />}
      {tab === "templates" && <TemplatesTab onUse={openWizard} />}
      {tab === "ideas" && <IdeasTab />}
      {tab === "roi" && <AttributionTab />}
      {tab === "analytics" && <AnalyticsTab query={analyticsQ} />}
      {tab === "settings" && <SettingsTab />}

      <CampaignWizard open={wizardOpen} template={wizardTemplate} onOpenChange={setWizardOpen} />
      <CampaignDetail campaignId={openCampaign} onOpenChange={(v) => !v && setOpenCampaign(null)} />
    </div>
  );
}

function Stat({ testId, label, value, accent, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testId} onClick={onClick} className="solid-surface rounded-[14px] p-4 text-left w-full">
      <p className="eyebrow">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color: accent ? "var(--scarlet-pos)" : "var(--scarlet-text)" }}>{value}</p>
    </Tag>
  );
}

function CampaignsTab({ onOpen, onNew }) {
  const queryClient = useQueryClient();
  const { me } = useAppData();
  const gymName = me?.organization_display_name || "Your Gym";
  const [previewFor, setPreviewFor] = useState(null);
  const campaignsQ = useQuery({ queryKey: ["marketing-campaigns"], queryFn: async () => (await api.get("/marketing/campaigns")).data.campaigns });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["marketing-campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["marketing-analytics"] });
  };
  const statusMutation = useMutation({
    mutationFn: ({ id, action }) => api.post(`/marketing/campaigns/${id}/status`, { action }),
    onSuccess: invalidate,
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const publishMutation = useMutation({
    mutationFn: (id) => api.post(`/marketing/campaigns/${id}/publish`),
    onSuccess: invalidate,
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (campaignsQ.isLoading) return <GymLoader label="Loading campaigns…" />;
  if (campaignsQ.isError) return <ErrorState error={campaignsQ.error} onRetry={() => campaignsQ.refetch()} title="Couldn't load campaigns" />;

  const campaigns = campaignsQ.data || [];
  if (!campaigns.length) {
    return (
      <div className="solid-surface rounded-[14px] p-8 text-center space-y-3">
        <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No campaigns yet.</p>
        <Button onClick={onNew} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
          <Plus size={15} /> Create your first campaign
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {campaigns.map((c) => (
        <div key={c.id} data-testid={MARKETING.campaignRow(c.id)} className="solid-surface rounded-[14px] p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <button onClick={() => onOpen(c.id)} className="text-left">
              <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>{c.name}</p>
              <p className="text-xs mt-0.5 capitalize" style={{ color: "var(--scarlet-text-secondary)" }}>
                {c.platform} · {c.objective.replace("_", " ")} · {rupees(c.daily_budget)}/day
              </p>
            </button>
            <div className="flex items-center gap-2">
              <StatusPill status={STATUS_PILL[c.status] || "expiring"} label={c.status} />
              {c.status === "active" && (
                <Button size="sm" variant="outline" data-testid={MARKETING.campaignPauseButton(c.id)} className="gap-1"
                  onClick={() => statusMutation.mutate({ id: c.id, action: "pause" })}><Pause size={13} /> Pause</Button>
              )}
              {c.status === "paused" && (
                <Button size="sm" variant="outline" data-testid={MARKETING.campaignResumeButton(c.id)} className="gap-1"
                  onClick={() => statusMutation.mutate({ id: c.id, action: "resume" })}><Play size={13} /> Resume</Button>
              )}
              {c.status === "draft" && (
                <Button size="sm" data-testid={MARKETING.campaignPublishButton(c.id)} className="text-white gap-1"
                  style={{ background: "var(--scarlet-accent)" }}
                  disabled={publishMutation.isPending}
                  onClick={() => publishMutation.mutate(c.id)}><Send size={13} /> Publish</Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3 pt-3" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
            <Metric label="Spend" value={rupees(c.metrics.spend)} />
            <Metric label="Leads" value={c.metrics.leads} />
            <Metric label="Cost / lead" value={rupees(c.metrics.cost_per_lead)} />
          </div>

          {!!c.creatives?.length && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
              <button
                data-testid={MARKETING.campaignPreviewToggle(c.id)}
                onClick={() => setPreviewFor(previewFor === c.id ? null : c.id)}
                className="text-xs font-semibold flex items-center gap-1.5"
                style={{ color: "var(--scarlet-accent)" }}
              >
                <Eye size={13} />
                {previewFor === c.id
                  ? "Hide preview"
                  : c.status === "draft" ? "See what you're publishing" : "See the live ad"}
              </button>
              {previewFor === c.id && (
                <div className="mt-3">
                  <AdPreview creatives={c.creatives} platform={c.platform} gymName={gymName} />
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-[11px]" style={{ color: "var(--scarlet-text-secondary)" }}>{label}</p>
      <p className="text-sm font-semibold tabular-nums" style={{ color: "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}

function TemplatesTab({ onUse }) {
  const templatesQ = useQuery({ queryKey: ["marketing-templates"], queryFn: async () => (await api.get("/marketing/templates")).data.templates });
  if (templatesQ.isLoading) return <GymLoader label="Loading templates…" />;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {(templatesQ.data || []).map((t) => (
        <div key={t.id} data-testid={MARKETING.templateCard(t.id)} className="solid-surface rounded-[14px] p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>{t.name}</p>
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full"
              style={{ background: "var(--scarlet-accent-tint)", color: "var(--scarlet-accent)" }}>{t.difficulty}</span>
          </div>
          <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{t.purpose}</p>
          <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
            ₹{t.budget_min}–{t.budget_max}/day · {t.duration_days} days · {t.platforms.join(" + ")}
          </p>
          <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{t.creatives.length} ready-made creatives</p>
          <Button size="sm" data-testid={MARKETING.templateUseButton(t.id)} onClick={() => onUse(t)}
            className="text-white gap-1.5 mt-auto self-start" style={{ background: "var(--scarlet-accent)" }}>
            <Sparkles size={13} /> Use this template
          </Button>
        </div>
      ))}
    </div>
  );
}

function IdeasTab() {
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const ideasQ = useQuery({
    queryKey: ["marketing-ideas", category, search],
    queryFn: async () => (await api.get("/marketing/ad-ideas", { params: { category: category || undefined, search: search || undefined } })).data,
  });

  const copy = (idea) => {
    navigator.clipboard?.writeText(`${idea.headline}\n\n${idea.body}`)
      .then(() => toast.success("Ad copy copied."))
      .catch(() => toast.error("Couldn't copy — select the text manually."));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <select
          data-testid={MARKETING.ideaCategoryFilter} value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-[8px] px-3 text-sm"
          style={{ border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)" }}
        >
          <option value="">All categories</option>
          {(ideasQ.data?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--scarlet-text-secondary)" }} />
          <Input data-testid={MARKETING.ideaSearchInput} className="pl-9" placeholder="Search headlines and copy…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{ideasQ.data?.total ?? 0} ideas</p>
      <div className="space-y-2">
        {(ideasQ.data?.ideas || []).map((i) => (
          <button
            key={i.id} data-testid={MARKETING.ideaRow(i.id)} onClick={() => copy(i)}
            className="solid-surface rounded-[12px] p-3.5 w-full text-left"
          >
            <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>{i.headline}</p>
            <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>{i.body}</p>
            <p className="text-[10px] mt-1.5 uppercase font-bold" style={{ color: "var(--scarlet-accent)" }}>Tap to copy</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab({ query }) {
  if (query.isLoading) return <GymLoader label="Loading analytics…" />;
  const campaigns = query.data?.campaigns || [];
  return (
    <div className="space-y-3">
      <div className="solid-surface rounded-[14px] p-4">
        <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
          Spend and leads below are measured from your own records — money you logged against a campaign, and
          real leads tagged to it. Impressions, clicks and CTR live inside Meta/Google and appear here once an
          ad account is connected.
        </p>
      </div>
      {campaigns.map((c) => (
        <div key={c.id} className="solid-surface rounded-[14px] p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>{c.name}</p>
            <StatusPill status={STATUS_PILL[c.status] || "expiring"} label={c.status} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
            <Metric label="Spend" value={rupees(c.metrics.spend)} />
            <Metric label="Leads" value={c.metrics.leads} />
            <Metric label="Cost / lead" value={rupees(c.metrics.cost_per_lead)} />
            <Metric label="Converted" value={c.metrics.converted} />
            <Metric label="Lead → member" value={c.metrics.lead_to_member_rate != null ? `${c.metrics.lead_to_member_rate}%` : "—"} />
          </div>
        </div>
      ))}
      {!campaigns.length && <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No campaign data yet.</p>}
    </div>
  );
}

function SettingsTab() {
  const queryClient = useQueryClient();
  const [labels, setLabels] = useState({ meta: "", google: "" });
  const accountsQ = useQuery({ queryKey: ["marketing-ad-accounts"], queryFn: async () => (await api.get("/marketing/ad-accounts")).data.accounts });

  const connectMutation = useMutation({
    mutationFn: ({ provider, label }) => api.post("/marketing/ad-accounts", { provider, account_label: label }),
    onSuccess: (res) => {
      toast.success(res.data.status_reason);
      queryClient.invalidateQueries({ queryKey: ["marketing-ad-accounts"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (accountsQ.isLoading) return <GymLoader label="Loading settings…" />;

  return (
    <div className="space-y-3">
      {(accountsQ.data || []).map((a) => (
        <div key={a.provider} className="solid-surface rounded-[14px] p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-semibold text-sm capitalize" style={{ color: "var(--scarlet-text)" }}>
              {a.provider === "meta" ? "Meta (Facebook & Instagram)" : "Google Ads"}
            </p>
            <span data-testid={MARKETING.adAccountStatus(a.provider)} className="text-[11px] font-semibold px-2 py-1 rounded-full"
              style={{ background: "var(--scarlet-accent-tint)", color: "var(--scarlet-accent)" }}>
              {a.status.replace(/_/g, " ")}
            </span>
          </div>
          {a.status_reason && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{a.status_reason}</p>}
          {a.account_label && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Account: {a.account_label}</p>}
          <div className="flex gap-2">
            <Input
              data-testid={a.provider === "meta" ? MARKETING.adAccountLabelInput : undefined}
              placeholder="Your ad account name" value={labels[a.provider]}
              onChange={(e) => setLabels({ ...labels, [a.provider]: e.target.value })}
            />
            <Button
              data-testid={MARKETING.adAccountConnect(a.provider)}
              disabled={!labels[a.provider]?.trim() || connectMutation.isPending}
              onClick={() => connectMutation.mutate({ provider: a.provider, label: labels[a.provider] })}
              className="text-white shrink-0" style={{ background: "var(--scarlet-accent)" }}
            >
              Save
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
